import React, { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { queueScan } from "@/lib/offlineDb";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ScanLine, Loader2, Navigation, MapPin, Copy, ImagePlus, Camera, RotateCcw, Save, Share2 } from "lucide-react";

/**
 * Quick Find — driver takes a photo of a parcel label / envelope / map screenshot,
 * AI extracts the address, backend geocodes it via postcodes.io (free), and the
 * driver gets instant "Open in Google Maps / Waze / What3Words" buttons plus
 * a one-tap "Save to library" for future difficult addresses.
 */
const AddressFinderQuickFind = () => {
  const nav = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const sharedFile = location.state?.sharedFile || null;
  const [result, setResult] = useState(location.state?.sharedScanResult || null);   // full server payload
  const [previewUrl, setPreviewUrl] = useState(() => sharedFile ? URL.createObjectURL(sharedFile) : null);
  const [file, setFile] = useState(sharedFile);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    await runScan(f);
  };

  const runScan = async (f) => {
    // Offline path — queue the photo and inform the driver
    if (!navigator.onLine) {
      try {
        await queueScan(f);
        toast.success("Photo saved offline — AI will scan it when you're back online");
        setResult({
          ok: false,
          offline: true,
          extracted: {},
          nav_urls: {},
        });
      } catch {
        toast.error("Could not save offline");
      }
      return;
    }

    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/address-finder/scan-label", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      setResult(data);
      if (!data?.ok) {
        toast.warning("Couldn't read a full address. Try a clearer photo.");
      } else {
        toast.success("Address extracted");
      }
    } catch (e) {
      // Network dropped during upload → fall back to offline queue
      if (e?.code === "ERR_NETWORK" || !navigator.onLine) {
        try {
          await queueScan(f);
          toast.success("Network failed — photo queued, will scan later");
          setResult({ ok: false, offline: true, extracted: {}, nav_urls: {} });
        } catch { toast.error("Scan failed and could not queue offline"); }
      } else {
        const detail = e?.response?.data?.detail || e?.message || "Scan failed";
        toast.error(detail);
      }
    }
    setBusy(false);
  };

  const reset = () => { setFile(null); setPreviewUrl(null); setResult(null); };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const saveToLibrary = async () => {
    if (!result?.extracted) return;
    const ex = result.extracted;
    if (!ex.postcode && !(result.geo?.lat && result.geo?.lng)) {
      toast.error("Can't save — no postcode or GPS extracted. Try a clearer photo.");
      return;
    }
    setBusy(true);
    try {
      const uuid = `qf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const payload = {
        title: ex.title || ex.property_name || ex.address_line_1 || ex.postcode || "Scanned address",
        property_name: ex.property_name || null,
        address_line_1: ex.address_line_1 || null,
        town: ex.town || null,
        postcode: ex.postcode || "",
        latitude: result.geo?.lat ?? null,
        longitude: result.geo?.lng ?? null,
        what3words: ex.what3words || null,
        driver_notes: [
          ex.recipient ? `Recipient: ${ex.recipient}` : null,
          ex.raw_text ? `Scanned from label:\n${ex.raw_text}` : null,
        ].filter(Boolean).join("\n\n") || null,
        offline_client_uuid: uuid,
      };
      const { data } = await api.post("/address-finder", payload);
      // Upload the scanned image too
      if (file && data?.id) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await api.post(`/address-finder/${data.id}/images?image_type=label`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
        } catch { /* ignore upload failure */ }
      }
      toast.success("Saved to library");
      nav(`/driver/address-finder/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const shareViaWhatsApp = () => {
    if (!result?.extracted) return;
    const ex = result.extracted;
    const lines = [
      ex.title || ex.property_name || "Difficult address",
      [ex.property_name, ex.address_line_1, ex.town, ex.postcode].filter(Boolean).join(", "),
    ];
    if (ex.recipient) lines.push(`For: ${ex.recipient}`);
    if (result.nav_urls?.google_maps_pin) lines.push(result.nav_urls.google_maps_pin);
    if (ex.what3words) lines.push(`///${ex.what3words} — ${result.nav_urls?.what3words}`);
    const text = encodeURIComponent(lines.filter(Boolean).join("\n"));
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const ex = result?.extracted;
  const nav_urls = result?.nav_urls || {};

  return (
    <div className="p-4 pt-6 space-y-4 pb-32">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="qf-back">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
      </Button>

      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Quick Find</div>
        <h1 className="font-heading text-2xl font-bold tracking-tight mt-1">Scan a label</h1>
        <p className="text-sm text-slate-500 mt-1">
          Snap the parcel label, envelope or Sat-Nav screen. We&apos;ll extract the address and open the route.
        </p>
      </div>

      {!previewUrl && (
        <Card className="p-6 rounded-2xl border-dashed border-2 border-brand-border">
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => cameraRef.current?.click()} className="h-24 flex-col bg-brand-red hover:bg-brand-red-hover"
                    data-testid="qf-take-photo">
              <Camera className="w-6 h-6 mb-1" />
              <span className="text-sm">Take photo</span>
            </Button>
            <Button variant="outline" onClick={() => galleryRef.current?.click()} className="h-24 flex-col"
                    data-testid="qf-from-gallery">
              <ImagePlus className="w-6 h-6 mb-1 text-brand-navy" />
              <span className="text-sm">From gallery</span>
            </Button>
          </div>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={onPick} />
          <input ref={galleryRef} type="file" accept="image/*" hidden onChange={onPick} />
          <div className="text-[11px] text-slate-500 mt-3 leading-relaxed">
            Works with printed labels, handwritten notes, Google Maps screenshots and What3Words.
          </div>
        </Card>
      )}

      {previewUrl && (
        <Card className="p-3 rounded-2xl border-brand-border relative">
          <img src={previewUrl} alt="" className="w-full max-h-72 object-contain rounded-lg" />
          <Button variant="outline" size="sm" onClick={reset}
                  className="absolute top-2 right-2" data-testid="qf-reset">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> New scan
          </Button>
        </Card>
      )}

      {busy && (
        <Card className="p-4 rounded-2xl bg-slate-50 border-brand-border flex items-center gap-3" data-testid="qf-busy">
          <Loader2 className="w-5 h-5 animate-spin text-brand-red" />
          <div className="text-sm">Reading label & locating on the map…</div>
        </Card>
      )}

      {!busy && result && !result.ok && (
        result.offline ? (
          <Card className="p-4 rounded-2xl bg-amber-50 border-amber-200" data-testid="qf-offline">
            <div className="text-sm font-medium text-amber-800 mb-1 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /> Saved offline
            </div>
            <div className="text-xs text-amber-700">
              Your photo is queued on this device. As soon as you have signal, the AI will read the label
              and this address will appear in your Address Finder library automatically.
            </div>
          </Card>
        ) : (
        <Card className="p-4 rounded-2xl bg-amber-50 border-amber-200" data-testid="qf-nofit">
          <div className="text-sm font-medium text-amber-800 mb-1">Couldn&apos;t extract a full address</div>
          <div className="text-xs text-amber-700">
            Try a clearer photo — make sure the postcode is in view and not blurred.
          </div>
          {ex?.raw_text && (
            <div className="text-xs mt-2 p-2 bg-white/60 rounded border border-amber-200 whitespace-pre-wrap">
              <span className="text-slate-500">Text we read: </span>{ex.raw_text}
            </div>
          )}
        </Card>
        )
      )}

      {!busy && result?.ok && ex && (
        <>
          <Card className="p-4 rounded-2xl border-brand-border space-y-2" data-testid="qf-result">
            {ex.postcode && (
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">{ex.postcode}</div>
            )}
            {ex.title && <div className="font-heading text-lg font-bold">{ex.title}</div>}
            <div className="text-sm text-slate-600">
              {[ex.property_name, ex.address_line_1, ex.address_line_2, ex.town]
                .filter(Boolean).join(" · ")}
            </div>
            {ex.recipient && (
              <div className="text-xs text-slate-500">
                Recipient: <span className="font-medium text-slate-700">{ex.recipient}</span>
              </div>
            )}
            {ex.what3words && (
              <div className="text-xs text-brand-navy font-mono">///{ex.what3words}</div>
            )}
            {result.geo?.lat != null && (
              <div className="text-xs text-slate-500 font-mono flex items-center gap-1 flex-wrap">
                <MapPin className="w-3 h-3" />
                {Number(result.geo.lat).toFixed(6)}, {Number(result.geo.lng).toFixed(6)}
                <button onClick={() => copy(`${result.geo.lat},${result.geo.lng}`)}
                        className="ml-1 text-brand-navy underline" data-testid="qf-copy-gps">
                  <Copy className="w-3 h-3 inline" />
                </button>
                {result.geo?.source === "osm-poi" ? (
                  <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] uppercase tracking-wider">
                    Pinned to {ex.property_name ? "property" : "POI"}
                  </span>
                ) : (
                  <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] uppercase tracking-wider">
                    Postcode area
                  </span>
                )}
              </div>
            )}
            {result.geo?.source === "osm-poi" && result.geo?.poi_distance_from_postcode_m != null && (
              <div className="text-[10px] text-slate-400">
                Matched via OpenStreetMap — {result.geo.poi_distance_from_postcode_m}m from postcode centre
              </div>
            )}
          </Card>

          {nav_urls.google_maps && (
            <a href={nav_urls.google_maps} target="_blank" rel="noreferrer">
              <Button className="w-full h-14 bg-brand-red hover:bg-brand-red-hover text-lg" data-testid="qf-open-gmaps">
                <Navigation className="w-6 h-6 mr-2" /> Open in Google Maps
              </Button>
            </a>
          )}

          <div className="grid grid-cols-3 gap-2">
            {nav_urls.waze && (
              <a href={nav_urls.waze} target="_blank" rel="noreferrer">
                <Button variant="outline" className="w-full" data-testid="qf-open-waze">Waze</Button>
              </a>
            )}
            {nav_urls.apple_maps && (
              <a href={nav_urls.apple_maps} target="_blank" rel="noreferrer">
                <Button variant="outline" className="w-full" data-testid="qf-open-apple">Apple Maps</Button>
              </a>
            )}
            {nav_urls.what3words && (
              <a href={nav_urls.what3words} target="_blank" rel="noreferrer">
                <Button variant="outline" className="w-full" data-testid="qf-open-w3w">W3W</Button>
              </a>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={shareViaWhatsApp} data-testid="qf-share">
              <Share2 className="w-4 h-4 mr-1.5" /> Share
            </Button>
            <Button onClick={saveToLibrary} disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700" data-testid="qf-save">
              <Save className="w-4 h-4 mr-1.5" /> Save to library
            </Button>
          </div>

          {ex.raw_text && (
            <details className="text-xs text-slate-500">
              <summary className="cursor-pointer">Show raw text we read</summary>
              <div className="mt-2 p-2 bg-slate-50 rounded whitespace-pre-wrap font-mono">{ex.raw_text}</div>
            </details>
          )}
        </>
      )}
    </div>
  );
};

export default AddressFinderQuickFind;
